import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { submitQuote } from "../lib/api";
import { toast } from "sonner";

export default function QuoteDialog({ open, onOpenChange, projectSlug = "", projectTitle = "" }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => {
    setForm({ name: "", email: "", company: "", phone: "", message: "" });
    setSuccess(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitQuote({ ...form, project_slug: projectSlug });
      setSuccess(true);
      toast.success("Quote request submitted. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      <DialogContent
        data-testid="quote-dialog"
        className="bg-[#0a0a0c] border border-white/10 text-white max-w-xl rounded-none p-0 overflow-hidden"
      >
        <div className="relative bg-[#050505] px-6 py-5 border-b border-white/10">
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-1">Request Quote</div>
          <DialogHeader className="space-y-1">
            <DialogTitle className="font-display text-2xl text-white">
              {projectTitle ? `${projectTitle} — Quote & Demo` : "Request Quote & Demo"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Tell us about your project. We'll respond within one business day with a tailored quote and a live demo.
            </DialogDescription>
          </DialogHeader>
        </div>

        {success ? (
          <div className="px-6 py-12 text-center">
            <CheckCircle2 className="w-14 h-14 text-[#3B82F6] mx-auto mb-4" />
            <h3 className="font-display text-xl mb-2">Request received</h3>
            <p className="text-zinc-400 text-sm max-w-sm mx-auto">
              Thank you. Our team has been notified and will reach out at <span className="text-white">{form.email}</span> shortly with quote details and a demo schedule.
            </p>
            <Button
              data-testid="quote-close-btn"
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-[#3B82F6] hover:bg-[#60A5FA] rounded-none"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="q-name" className="text-zinc-300 text-xs uppercase tracking-wider">Name *</Label>
                <Input
                  id="q-name"
                  data-testid="quote-input-name"
                  value={form.name}
                  onChange={handle("name")}
                  className="bg-black/40 border-white/10 rounded-none text-white focus-visible:ring-[#3B82F6]"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-email" className="text-zinc-300 text-xs uppercase tracking-wider">Email *</Label>
                <Input
                  id="q-email"
                  type="email"
                  data-testid="quote-input-email"
                  value={form.email}
                  onChange={handle("email")}
                  className="bg-black/40 border-white/10 rounded-none text-white focus-visible:ring-[#3B82F6]"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-company" className="text-zinc-300 text-xs uppercase tracking-wider">Company</Label>
                <Input
                  id="q-company"
                  data-testid="quote-input-company"
                  value={form.company}
                  onChange={handle("company")}
                  className="bg-black/40 border-white/10 rounded-none text-white focus-visible:ring-[#3B82F6]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-phone" className="text-zinc-300 text-xs uppercase tracking-wider">Phone</Label>
                <Input
                  id="q-phone"
                  data-testid="quote-input-phone"
                  value={form.phone}
                  onChange={handle("phone")}
                  className="bg-black/40 border-white/10 rounded-none text-white focus-visible:ring-[#3B82F6]"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-message" className="text-zinc-300 text-xs uppercase tracking-wider">What do you need? *</Label>
              <Textarea
                id="q-message"
                data-testid="quote-input-message"
                value={form.message}
                onChange={handle("message")}
                rows={4}
                placeholder="Briefly describe your goals, timeline, and any specific features."
                className="bg-black/40 border-white/10 rounded-none text-white focus-visible:ring-[#3B82F6]"
                required
              />
            </div>
            <p className="text-xs text-zinc-500">
              By submitting, you agree to be contacted by Operexa Gennex regarding your inquiry. A live demo will also be scheduled.
            </p>
            <Button
              type="submit"
              data-testid="quote-submit-btn"
              disabled={submitting}
              className="w-full bg-[#3B82F6] hover:bg-[#60A5FA] rounded-none h-11 font-semibold"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Request</>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
